// La variante es la unidad de inventario. La talla es solo su etiqueta.
//
// GEMELO de `app/src/lib/variantes.ts` (y de `supabase/functions/_shared/`).
// Tres copias y no una compartida porque los tres no comparten paquete: `admin`
// es Vite, `app` es Expo y las funciones corren en Deno. Lo vigilan dos redes:
// `scripts/guard-gemelos-catalogo.mjs` compara panel contra app, y
// `supabase/__tests__/gemelas.test.ts` compara app contra funciones.
//
// ⚠️ El panel solo usa `etiquetaVariante`. El resto del módulo viaja igual para
// que las copias sean comparables byte a byte: un gemelo a medias no se puede
// vigilar, y esa es toda la razón de que esto exista.
//
// POR QUÉ EXISTE
// --------------
// Hasta la serie del inventario la ropa se vendía por TEXTO: el cliente mandaba
// `size:'M'`, el checkout lo sellaba tal cual y el reparto descontaba de
// `products.stock`, el agregado. A partir de ahí manda `variant_id`: el
// descuento es un movimiento sobre una fila concreta, por id. `variant_label`,
// `size` y `color` se siguen sellando, pero solo para PINTAR la línea.

/** Una variante tal y como la devuelve `product_variants`. */
export interface Variante {
  id: string;
  product_id: string;
  attributes: Record<string, unknown> | null;
  position?: number | null;
  active?: boolean | null;
}

/** Lo que el cliente dice haber elegido para una línea. */
export interface EleccionCliente {
  variant_id?: string | null;
  size?: string | null;
  color?: string | null;
}

/**
 * El orden en que se leen los ejes de una etiqueta.
 *
 * ⚠️ Hace falta y no es cosmético: los `attributes` vienen de una columna
 * `jsonb`, y Postgres NORMALIZA el orden de las claves. Medido contra
 * producción: `{"talla":"M","color":"negro"}::jsonb` sale
 * `{"color": "negro", "talla": "M"}` — o sea COLOR PRIMERO. Sin este orden
 * explícito la etiqueta diría «Color negro · Talla M», y quien la lee espera la
 * talla delante.
 *
 * Los ejes que no estén aquí van detrás, alfabéticamente, para que el resultado
 * no dependa nunca del orden físico de las claves.
 */
const ORDEN_DE_EJES = ['talla', 'color'];

function ejesDe(attributes: Record<string, unknown> | null | undefined): [string, string][] {
  return Object.entries(attributes ?? {})
    .filter((par): par is [string, string] => typeof par[1] === 'string' && par[1] !== '')
    .sort(([a], [b]) => {
      const ia = ORDEN_DE_EJES.indexOf(a);
      const ib = ORDEN_DE_EJES.indexOf(b);
      const pa = ia < 0 ? ORDEN_DE_EJES.length : ia;
      const pb = ib < 0 ? ORDEN_DE_EJES.length : ib;
      return pa !== pb ? pa - pb : a.localeCompare(b, 'es');
    });
}

function attr(v: Variante, clave: string): string | null {
  const a = v.attributes;
  if (!a || typeof a !== 'object') return null;
  const valor = (a as Record<string, unknown>)[clave];
  return typeof valor === 'string' && valor ? valor : null;
}

/** La talla de la variante, o null si el producto no tiene ese eje. */
export function tallaDe(v: Variante): string | null {
  return attr(v, 'talla');
}

/** El color de la variante, o null. */
export function colorDe(v: Variante): string | null {
  return attr(v, 'color');
}

/**
 * ¿Esta variante tiene algún eje?
 *
 * ⚠️ Existe para que la pregunta del CHECKOUT no pase por una cadena de
 * presentación. Antes `exigeEleccion` preguntaba `etiquetaVariante(v) !== ''`, y
 * eso ataba la resolución de la compra al texto que se pinta: al unificar la
 * etiqueta con la del panel —que devuelve «Única» cuando no hay ejes— todo
 * producto habría parecido tener algo que elegir, el selector habría salido con
 * una única opción «Única» y `resolverVariante` se habría saltado su paso 2, el
 * de «si no hay ejes, la única variante». Resultado: 400 al comprar cualquier
 * producto sin tallas. Hoy no hay ninguno en producción —medido: 12 variantes en
 * 2 productos, todas con talla—, o sea que era una mina que se armaba sola.
 */
export function tieneEjes(v: Variante): boolean {
  return ejesDe(v.attributes).length > 0;
}

/**
 * Lo que se le enseña a la persona: `'Talla M'`, `'Talla M · Color negro'`.
 *
 * ⚠️ MISMO TEXTO EN EL PANEL Y EN LA APP, y es el motivo de que este módulo sea
 * gemelo. Antes la app decía solo los valores —`'M · negro'`— y encima se comía
 * cualquier eje que no fuera talla o color; el panel decía `'Talla M · Color
 * negro'`. El mismo producto se rotulaba de dos maneras según por dónde se
 * mirara, y un cliente que compra una camiseta tiene que saber que la M es de
 * talla.
 *
 * Una variante SIN ejes devuelve «Única»: es la fila del producto que no tiene
 * tallas, y en el inventario significa algo. En la app no se llega a pintar,
 * porque sin ejes no hay selector — lo decide `tieneEjes`, no este texto.
 */
export function etiquetaVariante(
  attributes: Record<string, unknown> | null | undefined
): string {
  const ejes = ejesDe(attributes);
  if (ejes.length === 0) return 'Única';
  return ejes
    .map(([eje, valor]) => `${eje.charAt(0).toUpperCase()}${eje.slice(1)} ${valor}`)
    .join(' · ');
}

/**
 * ¿Hay algo que elegir en este producto?
 *
 * Solo si alguna de sus variantes tiene ejes. Un producto con una única
 * variante `{}` —la botella de agua— no pregunta nada: su variante se resuelve
 * sola y el comprador no ve ningún selector.
 */
export function exigeEleccion(variantes: readonly Variante[]): boolean {
  return variantes.some(tieneEjes);
}

/**
 * De lo que dice el cliente a la fila de inventario que se va a descontar.
 *
 * El orden importa:
 *
 *   1. `variant_id`, si viene. Es lo que manda, y se comprueba que sea de ESTE
 *      producto: un id de otro producto se rechaza en vez de descontar de un
 *      inventario ajeno.
 *   2. Si no hay ejes, la única variante. No hay nada que preguntar.
 *   3. Y si no, por texto: talla y color. Es el camino de la app YA PUBLICADA,
 *      que no sabe nada de variantes y manda `size:'M'`. Sin este paso, subir
 *      la validación dejaría sin comprar a todo el que no se haya actualizado.
 *
 * Devuelve `null` cuando no se puede resolver, y el llamante decide: en el
 * checkout es un 400, porque vender una talla que no existe es peor que no
 * vender.
 *
 * `variantes` tiene que venir ya filtrada a las de UN producto.
 */
export function resolverVariante(
  variantes: readonly Variante[],
  eleccion: EleccionCliente
): Variante | null {
  if (variantes.length === 0) return null;

  const pedido = typeof eleccion.variant_id === 'string' ? eleccion.variant_id : '';
  if (pedido) {
    return variantes.find((v) => v.id === pedido) ?? null;
  }

  if (!exigeEleccion(variantes)) {
    return variantes[0] ?? null;
  }

  const candidatas = candidatasPorTexto(variantes, eleccion);

  // ⚠️ UNA, O NINGUNA. Nunca «la primera de varias».
  //
  // El texto solo sabe decir talla y color. Con un tercer eje en el catálogo
  // —el panel deja crear el que quieras: «capacidad», «material»— dos filas
  // como `{talla:'M', material:'algodón'}` y `{talla:'M', material:'poliéster'}`
  // cumplen las dos la misma condición, y un `find` devolvía LA PRIMERA: la
  // compra salía adelante y el reparto descontaba de la fila que el orden del
  // array hubiera puesto delante. Nadie elegía esa talla y nadie se enteraba de
  // que se había elegido por él.
  //
  // Devolver `null` corta la venta con un 400. Es peor para quien compra y
  // mejor para todos: mover existencias de la variante equivocada se descubre
  // semanas después, cuando el inventario no cuadra y ya no hay forma de saber
  // qué pasó.
  //
  // Hoy no puede pasar —medido el 5-oct-2026: 12 variantes en 2 productos,
  // único eje `talla`, y ningún producto con dos variantes de la misma talla—,
  // así que esto no cambia ninguna compra de las que existen.
  return candidatas.length === 1 ? candidatas[0] : null;
}

/**
 * Las variantes que encajan con lo que el cliente dijo POR TEXTO.
 *
 * Existe separada porque el llamante necesita distinguir «no hay ninguna» de
 * «hay más de una»: son dos averías distintas y dan dos respuestas distintas.
 * Una talla que no existe es cosa de quien compra —o de un carrito viejo—; dos
 * variantes que encajan es cosa del catálogo, y a quien compra no le queda nada
 * que hacer salvo actualizar la app para poder mandar `variant_id`.
 *
 * Es la ÚNICA definición de la regla: `resolverVariante` la usa, así que no
 * pueden discrepar.
 */
export function candidatasPorTexto(
  variantes: readonly Variante[],
  eleccion: EleccionCliente
): Variante[] {
  const talla = typeof eleccion.size === 'string' && eleccion.size ? eleccion.size : null;
  const color = typeof eleccion.color === 'string' && eleccion.color ? eleccion.color : null;
  if (!talla && !color) return [];

  return variantes.filter((v) => tallaDe(v) === talla && colorDe(v) === color);
}
